import {
  S3Client,
  ListObjectsV2Command,
  GetObjectCommand,
  PutObjectCommand,
  DeleteObjectCommand,
  CopyObjectCommand,
} from "@aws-sdk/client-s3";

import { CLOUD_CONFIG } from "./config";
import { getAWSCredentials } from "./auth";

import type { FileNode } from "./types";

// Reuse one S3Client across operations; rebuild only when the credentials
// actually change (i.e. getAWSCredentials refreshed them). getAWSCredentials
// caches creds itself, so this avoids both Cognito churn and client churn.
let cachedClient: { client: S3Client; accessKeyId: string } | null = null;

async function getS3Client(): Promise<S3Client> {
  const creds = await getAWSCredentials();
  if (cachedClient && cachedClient.accessKeyId === creds.accessKeyId) {
    return cachedClient.client;
  }
  const client = new S3Client({
    region: CLOUD_CONFIG.region,
    credentials: {
      accessKeyId: creds.accessKeyId,
      secretAccessKey: creds.secretAccessKey,
      sessionToken: creds.sessionToken,
    },
  });
  cachedClient = { client, accessKeyId: creds.accessKeyId };
  return client;
}

/**
 * List files and "folders" (common prefixes) under a given S3 prefix.
 */
export async function listDirectory(prefix: string): Promise<FileNode[]> {
  const s3 = await getS3Client();
  const nodes: FileNode[] = [];

  const response = await s3.send(
    new ListObjectsV2Command({
      Bucket: CLOUD_CONFIG.bucketName,
      Prefix: prefix,
      Delimiter: "/",
    }),
  );

  // Folders (common prefixes)
  for (const cp of response.CommonPrefixes ?? []) {
    const folderKey = cp.Prefix!;
    const name = folderKey.replace(prefix, "").replace(/\/$/, "");
    if (name) {
      nodes.push({ type: "folder", name, key: folderKey });
    }
  }

  // Files
  for (const obj of response.Contents ?? []) {
    if (obj.Key === prefix) {
      continue;
    } // skip the directory marker itself
    const name = obj.Key!.replace(prefix, "");
    if (name && !name.endsWith("/")) {
      nodes.push({
        type: "file",
        name,
        key: obj.Key!,
        lastModified: obj.LastModified,
        size: obj.Size,
      });
    }
  }

  return nodes;
}

/**
 * Download and parse an excalidraw file from S3. Returns the object's ETag so
 * callers can pass it back via saveFile's `ifMatch` for conflict detection on
 * shared files.
 */
export async function openFile(key: string): Promise<{
  elements: any[];
  appState: any;
  files: any;
  etag: string | null;
}> {
  const s3 = await getS3Client();

  const response = await s3.send(
    new GetObjectCommand({ Bucket: CLOUD_CONFIG.bucketName, Key: key }),
  );

  const body = await response.Body!.transformToString("utf-8");
  const parsed = JSON.parse(body);
  return {
    elements: parsed.elements ?? [],
    appState: parsed.appState ?? {},
    files: parsed.files ?? {},
    etag: response.ETag ?? null,
  };
}

/**
 * Save the current scene to S3 at the given key. When `opts.ifMatch` is set,
 * S3 will return 412 PreconditionFailed if the object's ETag has changed since
 * load — caller should treat that as a conflict (do not overwrite).
 */
export async function saveFile(
  key: string,
  data: { elements: any[]; appState: any; files: any },
  opts?: { ifMatch?: string },
): Promise<{ etag: string | null }> {
  const s3 = await getS3Client();

  const body = JSON.stringify({
    type: "excalidraw",
    version: 2,
    source: window.location.origin,
    elements: data.elements,
    appState: {
      // Only persist layout-relevant appState, not transient UI state
      viewBackgroundColor: data.appState.viewBackgroundColor,
      gridSize: data.appState.gridSize,
      gridStep: data.appState.gridStep,
    },
    files: data.files,
  });

  const response = await s3.send(
    new PutObjectCommand({
      Bucket: CLOUD_CONFIG.bucketName,
      Key: key,
      Body: body,
      ContentType: "application/json",
      IfMatch: opts?.ifMatch,
    }),
  );
  return { etag: response.ETag ?? null };
}

/**
 * Delete a file from S3.
 */
export async function deleteFile(key: string): Promise<void> {
  const s3 = await getS3Client();
  await s3.send(
    new DeleteObjectCommand({ Bucket: CLOUD_CONFIG.bucketName, Key: key }),
  );
}

/**
 * Rename a file by copying then deleting.
 */
export async function renameFile(
  oldKey: string,
  newKey: string,
): Promise<void> {
  const s3 = await getS3Client();
  await s3.send(
    new CopyObjectCommand({
      Bucket: CLOUD_CONFIG.bucketName,
      CopySource: `${CLOUD_CONFIG.bucketName}/${oldKey}`,
      Key: newKey,
    }),
  );
  await deleteFile(oldKey);
}

/**
 * Create a folder by uploading a zero-byte marker object.
 */
export async function createFolder(prefix: string): Promise<void> {
  const s3 = await getS3Client();
  const key = prefix.endsWith("/") ? prefix : `${prefix}/`;
  await s3.send(
    new PutObjectCommand({
      Bucket: CLOUD_CONFIG.bucketName,
      Key: key,
      Body: "",
      ContentType: "application/x-directory",
    }),
  );
}

/**
 * Ensure a filename ends with .excalidraw.
 */
export function normalizeFilename(name: string): string {
  if (!name.endsWith(".excalidraw")) {
    return `${name}.excalidraw`;
  }
  return name;
}
